import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FileText } from "lucide-react";
import { TspCard, TspTable, TspBadge, TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../hooks/useTspApiResource";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import notification from "../../../../helpers/notification";
import { formatDate } from "../../utils/tspFormat";
import DailyReportFormCard from "./components/DailyReportFormCard";
import TspBackButton from "../../components/common/TspBackButton";

const STATUS_TONE = { Submitted: "green", Pending: "amber" };

export default function TspDailyReportsPage() {
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.DAILY_REPORTS_MANAGE);
  const reports = useTspApiResource(() => TspApiService.listDailyReports({ limit: 50 }), []);
  const pending = useTspApiResource(() => TspApiService.listPendingDailyReports(), []);

  const handleSubmit = async (body) => {
    try {
      await TspApiService.createDailyReport(body);
      notification("success", "Daily report submitted.");
      reports.reload();
      pending.reload();
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "Could not submit this report.");
      notification("error", message);
      return { ok: false, message };
    }
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "journey", label: "Journey" },
    { key: "city", label: "City" },
    { key: "books", label: "Books", align: "right", render: (row) => (row.books == null ? "—" : row.books) },
    { key: "responses", label: "Public Response", render: (row) => (row.responses == null ? "—" : `${row.responses} recorded`) },
    { key: "status", label: "Status", render: (row) => <TspBadge tone={STATUS_TONE[row.status]}>{row.status}</TspBadge> },
  ];

  const renderTable = () => {
    if (reports.error) return <TspLoadError error={reports.error} onRetry={reports.reload} what="daily reports" />;
    if (!reports.data || !pending.data) return <div className="tsp-dashboard-loading">Loading…</div>;

    const submittedRows = reports.data.map((r) => ({
      id: `report-${r.id}`,
      date: formatDate(r.report_date),
      sortDate: r.report_date,
      journey: r.journey.journey_name,
      city: r.city_visit.location?.label || "—",
      books: r.linked_book_quantity,
      responses: r.linked_public_responses,
      status: "Submitted",
    }));
    const pendingRows = pending.data.map((v) => ({
      id: `pending-${v.city_visit_id}`,
      date: formatDate(v.visit_date),
      sortDate: v.visit_date,
      journey: v.journey.journey_name,
      city: v.location?.label || "—",
      books: null,
      responses: null,
      status: "Pending",
    }));
    const rows = [...submittedRows, ...pendingRows].sort((a, b) => (a.sortDate < b.sortDate ? 1 : -1));

    if (rows.length === 0) return <TspEmptyState icon={FileText} title="No city visits yet" message="Reports appear here once a journey records its first city visit." />;
    return <TspTable columns={columns} rows={rows} rowKey="id" />;
  };

  return (
    <div className="tsp-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Daily Reporting</h1>
          <TspBreadcrumb items={["Dashboard", "Daily Reports"]} />
        </div>
        <TspBackButton fallback="/tsp" />
      </div>

      {canManage && (pending.error ? <TspLoadError error={pending.error} onRetry={pending.reload} what="pending visits" /> : <DailyReportFormCard pending={pending.data || []} pendingLoading={pending.loading} onSubmit={handleSubmit} />)}

      <TspCard title="Daily Reports">{renderTable()}</TspCard>

      <ToastContainer />
    </div>
  );
}
